import { useEffect, useState, type ReactNode, type JSX } from 'react';
import { useDispatch } from 'react-redux';
import { Loader2 } from 'lucide-react';
import { tokenStore } from '@lib/tokenStore';
import { services } from '@config/services';
import { userAuthenticated, userSignedOut } from './slice';
import { readSessionUser } from './sessionUserStorage';
import type { LoginResponse } from './types';

/**
 * Settles the auth state on app boot.
 *
 * Common reload path: tokenStore + the auth slice already rehydrated the access
 * token and `currentUser` from sessionStorage, so if the token is still valid we
 * render immediately and make NO network call. This is what makes a rapid
 * double-refresh safe — previously every reload fired `/v1/refresh`, and two
 * reloads racing on the same (single-use, rotated) refresh token caused the
 * backend to reject the second one and log the user out.
 *
 * Fallback path: no live access token but a refresh token survives in
 * localStorage (e.g. token expired, or a brand-new tab where sessionStorage is
 * empty). We attempt a single `/v1/refresh` and only let routing settle once we
 * know whether the user is still signed in.
 *
 * Phase 1 model (per MIGRATION_PLAN §1): refresh token lives in localStorage
 * (interim). When the backend cookie cutover ships, the body becomes `{}`
 * and the cookie is sent automatically; the rest of this component is
 * unchanged.
 */

interface Props {
  children: ReactNode;
}

type Phase = 'pending' | 'ready';

/**
 * True when a non-expired access token AND a persisted user are both present —
 * i.e. the session was fully restored from sessionStorage and no refresh is
 * needed. Requiring both guards the degenerate case where one half of the
 * snapshot was cleared (we then fall through to the refresh path).
 */
function hasLiveSession(): boolean {
  return !tokenStore.isAccessTokenExpired() && readSessionUser() !== null;
}

export function AuthBootstrap({ children }: Props): JSX.Element {
  const dispatch = useDispatch();
  const [phase, setPhase] = useState<Phase>(() => {
    if (hasLiveSession()) return 'ready';
    return tokenStore.getRefreshToken() ? 'pending' : 'ready';
  });

  useEffect(() => {
    if (phase !== 'pending') return;

    let cancelled = false;
    (async () => {
      const refreshToken = tokenStore.getRefreshToken();
      if (!refreshToken) {
        if (!cancelled) setPhase('ready');
        return;
      }
      try {
        const response = await fetch(`${services.user}/v1/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        if (!response.ok) throw new Error(`refresh failed: ${response.status}`);
        const data = (await response.json()) as LoginResponse & { isError?: boolean };
        if (data.isError || !data.accessToken || !data.expiresIn) {
          throw new Error('refresh response missing tokens');
        }
        if (cancelled) return;
        tokenStore.setAccessToken(data.accessToken, data.expiresIn);
        if (data.refreshToken) tokenStore.setRefreshToken(data.refreshToken);
        const {
          accessToken: _a,
          refreshToken: _r,
          expiresIn: _e,
          tokenType: _t,
          scope: _s,
...user
        } = data;
        dispatch(userAuthenticated(user));
      } catch {
        // Refresh failed — the persisted session is unrecoverable. Clear the
        // tokens AND the persisted user: the auth slice now hydrates
        // currentUser from sessionStorage, so clearing tokens alone would
        // leave a "zombie" logged-in shell whose every request 401/403s.
        // userSignedOut wipes currentUser (+ sessionStorage), so AppLayout
        // redirects to /signin.
        if (!cancelled) {
          tokenStore.clearAll();
          dispatch(userSignedOut());
        }
      } finally {
        if (!cancelled) setPhase('ready');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [phase, dispatch]);

  if (phase === 'pending') {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-background text-muted-foreground"
        data-testid="auth-bootstrap-loading"
      >
        <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
        <span className="sr-only">Signing you in…</span>
      </div>
    );
  }

  return <>{children}</>;
}
